import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { format } from "date-fns";
import { observer } from "mobx-react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import GenericInfiniteTable, {
  ColumnDef,
} from "../../../molecules/PatientRxTables/GenericInfiniteTable";
import useStore from "../../../helpers/useStore";
import { RegistryContext } from "../../../stores/Registry/registry.provider";
import { registryKeys } from "../../../stores/Registry/registry.keys";
import {
  GetDocumentationStatsWindowEnum,
  GetRegistryTreatmentNotesSortOrderEnum,
  GetUndocumentedTreatmentsWindowEnum,
  RegistryDocumentationStatsDto,
  RegistryTreatmentNoteRowDto,
  UndocumentedTreatmentDto,
} from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import {
  type DocumentationStatsWindow,
} from "../../../stores/Registry/registryStore";
import { useLocationId } from "../../../stores/User/User.hooks";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { PatientCommands } from "../../../stores/Patients/patient.commands";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import FlatCard from "../../../atoms/FlatCard";

/**
 * Full-name label `Last, First`. Nickname is intentionally not used here:
 * the registry view requires the full name regardless of whether a user has a
 * nickname set.
 */
const formatFullName = (u: {
  firstName?: string | null;
  lastName?: string | null;
}): string => {
  const last = u.lastName ?? "";
  const first = u.firstName ?? "";
  if (!last && !first) return "-";
  return `${last}${last && first ? ", " : ""}${first}`;
};

const WINDOW_LABELS: Record<DocumentationStatsWindow, string> = {
  [GetDocumentationStatsWindowEnum.Last3Months]: "Letzte 3 Monate",
  [GetDocumentationStatsWindowEnum.LastYear]: "Letztes Jahr",
};

/**
 * Pick a colour for the documentation-completeness bar based on the
 * percentage. The app theme overrides MUI primary to white, so the default
 * LinearProgress bar would render invisibly — we always pass a concrete
 * colour here.
 */
const colorForPercentage = (
  pct: number,
): { bar: string; track: string } => {
  if (pct >= 95) return { bar: "#2e7d32", track: "#c8e6c9" }; // green
  if (pct >= 80) return { bar: "#9acd32", track: "#e6f0c2" }; // yellow-green
  if (pct >= 60) return { bar: "#ed6c02", track: "#ffe0b2" }; // orange
  return { bar: "#d32f2f", track: "#ffcdd2" }; // red
};

const RegistryTreatmentNotesTab: React.FC = observer(() => {
  const RegistryStore = useStore(RegistryContext);
  const PatientsStore = useStore(PatientsContext);
  const { clinicalDocumentation } = useApi();
  const {
    treatmentNoteFilters: filters,
    setTreatmentNoteFilters,
    treatmentNoteSelectedIndex: selectedIndex,
    setTreatmentNoteSelectedIndex: setSelectedIndex,
    documentationStatsWindow,
    setDocumentationStatsWindow,
  } = RegistryStore;

  const locationId = useLocationId();
  const navigate = useNavigate();
  const { navigateToPatient } = PatientCommands(PatientsStore);

  // Whether the "Undokumentierte Behandlungen" expander panel is open.
  // Collapsed by default; only visible / enabled when a therapist filter is set.
  const [showUndocumented, setShowUndocumented] = useState(false);

  // ---- Effective filters --------------------------------------------------
  const effectiveFilters = useMemo(
    () => ({ ...filters, location: filters.location ?? locationId }),
    [filters, locationId],
  );

  // ---- Therapist stats (drives both the stats panel and the therapist
  //      filter dropdown so we never show non-therapists or users outside
  //      the current location) -------------------------------------------
  const {
    data: stats,
    isLoading: isLoadingStats,
  } = useQuery<RegistryDocumentationStatsDto[]>({
    queryKey: registryKeys.documentationStats.detail(
      effectiveFilters.location,
      documentationStatsWindow,
    ),
    queryFn: () =>
      clinicalDocumentation.getDocumentationStats(
        effectiveFilters.location,
        documentationStatsWindow,
      ),
    enabled: !!effectiveFilters.location,
    staleTime: moment.duration(2, "minutes").asMilliseconds(),
    meta: { errorMessage: "Doku-Statistik konnte nicht geladen werden" },
  });

  const therapistOptions = useMemo(
    () =>
      (stats ?? []).map((t) => ({
        id: t.userId,
        label: formatFullName(t),
      })),
    [stats],
  );

  // ---- Treatment notes infinite query --------------------------------------
  const {
    data,
    isFetchingNextPage,
    isLoading,
    hasNextPage: hasMoreData,
    fetchNextPage,
  } = useInfiniteQuery<RegistryTreatmentNoteRowDto[], Error>({
    queryKey: registryKeys.treatmentNotes.filtered(effectiveFilters),
    queryFn: ({ pageParam = 1 }) =>
      clinicalDocumentation.getRegistryTreatmentNotes(
        effectiveFilters.limit,
        pageParam as number,
        GetRegistryTreatmentNotesSortOrderEnum.Desc,
        effectiveFilters.location,
        effectiveFilters.therapistId,
      ),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length >= effectiveFilters.limit ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    staleTime: moment.duration(2, "minutes").asMilliseconds(),
    enabled: !!effectiveFilters.location,
    meta: { errorMessage: "Behandlungsnotizen konnten nicht geladen werden" },
  });

  const rows: RegistryTreatmentNoteRowDto[] = data?.pages.flat() ?? [];

  // ---- Undocumented treatments for the selected therapist ------------------
  const {
    data: undocumented,
    isLoading: isLoadingUndocumented,
  } = useQuery<UndocumentedTreatmentDto[]>({
    queryKey: registryKeys.undocumentedTreatments.detail(
      filters.therapistId ?? "",
      documentationStatsWindow,
    ),
    queryFn: () =>
      clinicalDocumentation.getUndocumentedTreatments(
        filters.therapistId!,
        documentationStatsWindow as unknown as GetUndocumentedTreatmentsWindowEnum,
      ),
    enabled: !!filters.therapistId && showUndocumented,
    staleTime: moment.duration(2, "minutes").asMilliseconds(),
    meta: { errorMessage: "Undokumentierte Behandlungen konnten nicht geladen werden" },
  });

  // ---- Navigation ---------------------------------------------------------
  // Use the canonical PatientCommands.navigateToPatient helper so URL
  // projection / store hydration stays consistent with the rest of the app.
  const openPatient = (patientId: number, rxId?: string | null) => {
    navigateToPatient(
      navigate,
      patientId,
      rxId ?? null,
      rxId ? PATIENT_SUBTABS.PRESCRIPTIONS : undefined,
    );
  };

  const handleRowClick = (row: RegistryTreatmentNoteRowDto) => {
    if (row.patientId) openPatient(row.patientId, row.rxId);
  };

  // ---- Columns ------------------------------------------------------------
  const columns: ColumnDef<RegistryTreatmentNoteRowDto & { id: number }>[] = useMemo(
    () => [
      {
        key: "patientId",
        header: "Patienten-ID",
        render: (r) => r.patientId ?? "-",
        width: "100px",
      },
      {
        key: "rxNumber",
        header: "Rezeptnummer",
        render: (r) => r.rxNumber ?? "-",
      },
      {
        key: "rxDate",
        header: "Rezeptdatum",
        render: (r) => (r.rxDate ? format(new Date(r.rxDate), "dd.MM.yyyy") : "-"),
      },
      {
        key: "date",
        header: "Notiz-Datum",
        render: (r) => (r.date ? format(new Date(r.date), "dd.MM.yyyy") : "-"),
      },
      {
        key: "note",
        header: "Notiz",
        // Notes can be multi-line free text; let them wrap and use the full
        // width the table column gives them rather than truncating with
        // ellipsis.
        render: (r) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {r.note || "-"}
          </Typography>
        ),
      },
      {
        key: "therapist",
        header: "Therapeut:in",
        render: (r) =>
          formatFullName({
            firstName: r.therapistFirstName,
            lastName: r.therapistLastName,
          }),
      },
    ],
    [],
  );

  // ---- Header: filter + stats panel ---------------------------------------
  const headerCard = (
    <FlatCard
      title="Tagesdoku"
      subtitle={`Dokumentationsquote je Therapeut:in – ${WINDOW_LABELS[documentationStatsWindow]}`}
      sx={{ mb: 1 }}
      sxContent={{ p: 1.5 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Filters + window toggle */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption">Therapeut:in</Typography>
            <Select
              size="small"
              value={filters.therapistId ?? ""}
              onChange={(e) =>
                setTreatmentNoteFilters({
                  therapistId: (e.target.value as string) || undefined,
                })
              }
              displayEmpty
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">
                <em>Alle</em>
              </MenuItem>
              {therapistOptions.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption">Zeitraum (Statistik)</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={documentationStatsWindow}
              onChange={(_, value: DocumentationStatsWindow | null) => {
                if (value) setDocumentationStatsWindow(value);
              }}
            >
              <ToggleButton value="last3Months">
                {WINDOW_LABELS.last3Months}
              </ToggleButton>
              <ToggleButton value="lastYear">{WINDOW_LABELS.lastYear}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Per-therapist documentation stats */}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "white" }}>
                  Therapeut:in
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, backgroundColor: "white" }}
                  align="right"
                >
                  Behandlungen
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, backgroundColor: "white" }}
                  align="right"
                >
                  Mit Doku
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, minWidth: 180, backgroundColor: "white" }}
                  align="right"
                >
                  Quote
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingStats && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoadingStats && (stats ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Keine Therapeut:innen an diesem Standort
                  </TableCell>
                </TableRow>
              )}
              {(stats ?? []).map((row, i) => {
                const hasData = row.totalTreatments > 0;
                return (
                  <TableRow
                    key={row.userId}
                    sx={{
                      backgroundColor: i % 2 === 0 ? "white" : "grey.100",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setTreatmentNoteFilters({ therapistId: row.userId })
                    }
                  >
                    <TableCell>{formatFullName(row)}</TableCell>
                    <TableCell align="right">{row.totalTreatments}</TableCell>
                    <TableCell align="right">{row.treatmentsWithNotes}</TableCell>
                    <TableCell align="right">
                      {hasData ? (
                        (() => {
                          const pct = Math.max(0, Math.min(100, row.percentage));
                          const palette = colorForPercentage(pct);
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Box sx={{ width: 100 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: palette.track,
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: palette.bar,
                                    },
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  minWidth: 48,
                                  textAlign: "right",
                                  color: palette.bar,
                                  fontWeight: 600,
                                }}
                              >
                                {row.percentage.toFixed(1)}%
                              </Typography>
                            </Box>
                          );
                        })()
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          –
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Undocumented treatments expander (only when a therapist is picked) */}
        {filters.therapistId && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 0.5,
              }}
            >
              <Typography variant="subtitle2">
                Undokumentierte Behandlungen
                {undocumented ? ` (${undocumented.length})` : ""}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => setShowUndocumented((v) => !v)}
                  endIcon={showUndocumented ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {showUndocumented ? "Einklappen" : "Anzeigen"}
                </Button>
              </Box>
            </Box>
            <Collapse in={showUndocumented} unmountOnExit>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "white" }}>
                        Datum
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "white" }}>
                        Patient:in
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "white" }}>
                        Rezeptnummer
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: "white" }}>
                        Therapie
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingUndocumented && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoadingUndocumented && (undocumented ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Alle Behandlungen sind dokumentiert
                        </TableCell>
                      </TableRow>
                    )}
                    {(undocumented ?? []).map((t, i) => {
                      const canOpen = !!t.patientId;
                      return (
                        <TableRow
                          key={t.treatmentId}
                          hover={canOpen}
                          onClick={() => {
                            if (canOpen) openPatient(t.patientId!, t.rxId);
                          }}
                          sx={{
                            backgroundColor: i % 2 === 0 ? "white" : "grey.100",
                            cursor: canOpen ? "pointer" : "default",
                          }}
                        >
                          <TableCell>
                            {t.start ? format(new Date(t.start), "dd.MM.yyyy") : "-"}
                          </TableCell>
                          <TableCell>
                            {formatFullName({
                              firstName: t.patientFirstName,
                              lastName: t.patientLastName,
                            })}
                          </TableCell>
                          <TableCell>{t.rxNumber ?? "-"}</TableCell>
                          <TableCell>
                            {t.therapyAbbreviation || t.therapyId || "-"}
                            {t.therapyDescription && t.therapyAbbreviation ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {t.therapyDescription}
                              </Typography>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        )}
      </Box>
    </FlatCard>
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <GenericInfiniteTable<any>
        data={rows}
        columns={columns}
        hasMoreData={!!hasMoreData}
        fetchMoreData={fetchNextPage}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        onRowClick={handleRowClick}
        scrollId="registryTreatmentNotesScroll"
        toolbar={headerCard}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        sortBy={filters.sortBy as any}
        emptyMessage="Keine Behandlungsnotizen gefunden"
      />
    </Box>
  );
});

export default RegistryTreatmentNotesTab;
